import { useEffect, useMemo, useRef, useState } from 'react';
import type { Theme } from '@goodtime/engine';
import {
  ALL_ITEMS,
  FINAL_LAP_TRIGGER_ROUND,
  isFinalLap,
  isUnderdogPosition,
  rankSeatsByTokens,
  rollLoadoutForPosition,
  type Biome,
  type ItemId,
  type WheelObservableState,
  type WheelSeatView,
  ITEMS,
  ITEM_TIER_META,
} from '@goodtime/shared';
import { submitWheelCommand } from '../../api/wheelCommand';
import { Icon } from '../Icon';
import { CastAvatar } from './CastAvatar';
import { FinalLapOverlay } from './FinalLapOverlay';
import { WheelStakeSlider } from './WheelStakeSlider';

/**
 * Local keyframes — extends the global ANIMATION_KEYFRAMES set
 * (defined in WorldMapView) with shimmer effects specific to the
 * loadout reveal. Mounted as a {@code <style>} block at the top of
 * the component output.
 */
const LOADOUT_KEYFRAMES = `
  @keyframes gtLegendaryShimmer {
    0%, 100% {
      box-shadow: 0 0 6px rgba(180, 117, 255, 0.25), inset 0 0 0 1px rgba(180, 117, 255, 0.2);
      border-color: rgba(180, 117, 255, 0.45);
    }
    50% {
      box-shadow: 0 0 22px rgba(180, 117, 255, 0.75), inset 0 0 0 1px rgba(180, 117, 255, 0.55);
      border-color: rgba(180, 117, 255, 0.9);
    }
  }
  @keyframes gtUnderdogSpotlight {
    0%, 100% {
      box-shadow: 0 0 8px rgba(255, 201, 64, 0.18);
    }
    50% {
      box-shadow: 0 0 18px rgba(255, 201, 64, 0.42);
    }
  }
  /* Tier-reveal flair — three increasingly theatrical "land" treatments.
     COMMON uses the existing gtPop (no extra ring). UNCOMMON adds a soft
     coin-burst ring. RARE punches a gold ring + screen-shake hint via
     the wrapper's transform. LEGENDARY layers a fullscreen-ish flash
     handled by a sibling element + a bigger ring + a slow wash. */
  @keyframes gtTierRingUncommon {
    0% { transform: scale(0.6); opacity: 0; border-width: 6px; }
    35% { opacity: 0.85; }
    100% { transform: scale(1.7); opacity: 0; border-width: 1px; }
  }
  @keyframes gtTierRingRare {
    0% { transform: scale(0.5); opacity: 0; border-width: 8px; }
    25% { opacity: 1; }
    100% { transform: scale(2.2); opacity: 0; border-width: 1px; }
  }
  @keyframes gtTierRingLegendary {
    0% { transform: scale(0.5); opacity: 0; border-width: 10px; }
    20% { opacity: 1; }
    100% { transform: scale(3.0); opacity: 0; border-width: 1px; }
  }
  @keyframes gtTierShakeRare {
    0%, 100% { transform: translate(0, 0); }
    20% { transform: translate(-3px, 1px); }
    40% { transform: translate(2px, -2px); }
    60% { transform: translate(-2px, 2px); }
    80% { transform: translate(1px, -1px); }
  }
  @keyframes gtTierShakeLegendary {
    0%, 100% { transform: translate(0, 0); }
    15% { transform: translate(-5px, 2px); }
    30% { transform: translate(4px, -3px); }
    45% { transform: translate(-4px, 3px); }
    60% { transform: translate(3px, -2px); }
    75% { transform: translate(-2px, 1px); }
  }
  @keyframes gtTierFlashLegendary {
    0% { opacity: 0; }
    10% { opacity: 0.85; }
    100% { opacity: 0; }
  }
`;

/**
 * Biome-panorama backdrop lookup. Copied from {@link WheelStakeView}
 * for now — extract to a shared helper if a fourth caller appears
 * (per the convention noted in {@code WheelStakeView}).
 */
const BACKDROP_MODULES = import.meta.glob<{ default: string }>(
  '../../assets/map_bg/*.png',
  { eager: true, query: '?url' },
);
const BACKDROP_BY_BIOME: Partial<Record<Biome['id'], string>> = Object.fromEntries(
  Object.entries(BACKDROP_MODULES)
    .map(([path, mod]) => {
      const id = path.match(/([^/]+)\.png$/)?.[1] ?? null;
      return id ? [id, mod.default] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null),
);

function biomeIdForTheme(theme: Theme | null | undefined): Biome['id'] {
  switch (theme?.id) {
    case 'forest':
      return 'kingdom';
    case 'underwater':
      return 'cove';
    case 'frost':
      return 'frost';
    case 'frontier':
      return 'frontier';
    case 'neon':
      return 'neon';
    default:
      return 'kingdom';
  }
}

/**
 * Reel-spin durations (ms) per slot index. Reel 3 is the "anchor" —
 * spins longest with the most drama. Tuned from MK pre-race cadence:
 * fast first land, building tension, big finish.
 */
const REEL_SPIN_DURATIONS_MS: readonly [number, number, number] = [1500, 2200, 3200];

/** How fast the spinning glyph cycles during the spin animation. */
const REEL_TICK_INTERVAL_MS = 80;

/** Visual size variants — large for the local player's row, small for
 *  the cast-row mirrors of rival rolls. */
type ReelSize = 'large' | 'small';

const REEL_DIMS: Record<ReelSize, { tile: number; glyph: number; nameFs: number }> = {
  large: { tile: 84, glyph: 38, nameFs: 10 },
  small: { tile: 36, glyph: 20, nameFs: 0 },
};

export interface WheelLoadoutViewProps {
  state: WheelObservableState;
  /** Local player's seat (null = audience-only viewing). */
  selfSeatId?: number | null;
  /** Pixi/biome theme of the room — drives the panorama backdrop so
   *  the loadout phase stays visually continuous with the lobby. */
  theme?: Theme | null;
  /** F2.2 — onboarding curriculum step (1..5). Reserved for future
   *  onboarding ramps; phase-1 prove-out ignores it (every match shows
   *  the same six-item pool). */
  onboardingSession?: number | null;
  /** Live-mode wiring. Both required for the stake slider to fire
   *  {@code SET_STAKE}; null in mock / audience mode → the slider
   *  renders read-only. */
  roomId?: string | null;
  playerId?: string | null;
}

/**
 * U5 (redesign) — MK-style loadout reveal. Replaces the legacy Stake
 * allocator (Shield/Lance/Reach + per-pot +/-) with a 3-reel item-roll
 * moment. See <code>plans/gear-up-for-the-purrfect-biscuit.md</code>
 * for the full design.
 *
 * <p><b>Phase 2 scope (this file):</b> position-weighted rolls via
 * {@link rollLoadoutForPosition}. Each seat's standing (1 = leader,
 * larger = lower) drives its tier-weight tuple. The full 20-item
 * catalog is now visible — last-place rolls are 15× more likely to
 * land a Legendary than the leader. Items still don't fire (no effect
 * resolution); the felt-test gate is "does starting position determine
 * the outcome too much, or just enough?"
 *
 * <p><b>Phase 1 (kept from previous turn):</b> 3-reel reveal,
 * inventory HUD, cast-row mirror, biome backdrop.
 *
 * <p><b>Out of phase 2 scope:</b> tap-to-deploy + targeting (phase 3),
 * Final Lap (phase 3), meta-progression (phase 4), server-side
 * deterministic rolls (phase 2 wrap-up after felt-test).
 */
export function WheelLoadoutView({
  state,
  selfSeatId = null,
  theme,
  roomId = null,
  playerId = null,
}: WheelLoadoutViewProps) {
  const matchNumber = state.matchNumber ?? 1;

  // Phase-3 — Final Lap kicks in at round 7+. The flag swaps
  // {@link RUBBER_BAND_TABLE} for {@link FINAL_LAP_RUBBER_BAND_TABLE}
  // (every player guaranteed Rare-or-better) and forces every reel to
  // shimmer (everyone has legendary weight, so everyone is a viable
  // underdog from the rubber-band's POV).
  const finalLap = isFinalLap(matchNumber);

  // Phase-2 rubber-banding: rank seats by tokens, then roll each one
  // from a position-weighted tier table. Both maps are stable for the
  // useMemo cycle in {@link useSeatRolls}. Phase-3 threads the
  // finalLap flag through so the right table is consulted.
  const seatPositions = useMemo(() => rankSeatsByTokens(state.seats), [state.seats]);
  const seatRolls = useSeatRolls(state.seats, matchNumber, seatPositions, finalLap);

  const selfSeat = selfSeatId !== null
    ? state.seats.find((s) => s.seatId === selfSeatId) ?? null
    : null;
  const selfRoll = selfSeat ? seatRolls.get(selfSeat.seatId) ?? null : null;
  const selfPosition = selfSeat ? seatPositions.get(selfSeat.seatId) ?? 1 : 1;
  const selfIsUnderdog = isUnderdogPosition(selfPosition, finalLap);

  // Final Lap overlay — fires once at the start of round 7. Round 8
  // does NOT re-fire it (the trigger is the entry, not the state).
  // Composite key includes matchNumber so re-renders during the same
  // round don't restart the animation.
  const finalLapTriggerKey =
    matchNumber === FINAL_LAP_TRIGGER_ROUND ? `final-lap-${matchNumber}` : null;

  // Phase-3 — tracks a single trigger key for the fullscreen LEGENDARY
  // flash. Set when ANY self reel lands on a Legendary item; the flash
  // overlay's internal key-comparison plays its sequence once and
  // unmounts. The cap of one legendary per roll means this fires at
  // most once per match, but if the cap is ever lifted the deduping
  // here keeps the flash a one-shot per match.
  const [legendaryFlashKey, setLegendaryFlashKey] = useState<string | null>(null);
  // Phase-4 — track which of the player's three reels have landed.
  // Drives the tap-to-deploy strip: a slot is tappable only once its
  // reel finishes spinning.
  const [selfLandedSlots, setSelfLandedSlots] = useState<ReadonlyArray<boolean>>(() => [false, false, false]);

  // Reset transient state on a new match.
  const matchKey = `${matchNumber}-${selfSeat?.seatId ?? 'audience'}`;
  useEffect(() => {
    setLegendaryFlashKey(null);
    setSelfLandedSlots([false, false, false]);
  }, [matchKey]);

  const handleSelfReelLand = (idx: number, itemId: ItemId) => {
    setSelfLandedSlots((prev) => {
      if (prev[idx]) return prev;
      const next = prev.slice();
      next[idx] = true;
      return next;
    });
    if (ITEMS[itemId].tier === 'LEGENDARY') {
      // Use a per-match key so the fullscreen flash fires exactly
      // once per match even if multiple reels land on legendary
      // (defensive — the cap caps at 1 today but this stays correct
      // if the cap is ever raised).
      setLegendaryFlashKey((prev) => prev ?? `${matchKey}-legendary`);
    }
  };

  // Biome panorama backdrop — keeps visual continuity with the lobby
  // and the legacy stake screen.
  const backdropBiomeId = biomeIdForTheme(theme);
  const backdropUrl = BACKDROP_BY_BIOME[backdropBiomeId] ?? null;

  const otherSeats = state.seats.filter(
    (s) => s.kind !== 'EMPTY' && s.seatId !== (selfSeat?.seatId ?? -1),
  );

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        overflowY: 'auto',
        background:
          'radial-gradient(ellipse at top, #1B2C56 0%, #0B1838 60%, #060d22 100%)',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui',
        padding: '12px 12px 80px',
      }}
    >
      {/* Loadout-local keyframes (gtLegendaryShimmer, gtUnderdogSpotlight)
          on top of the global ANIMATION_KEYFRAMES from WorldMapView. */}
      <style>{LOADOUT_KEYFRAMES}</style>
      {backdropUrl ? (
        <img
          src={backdropUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.4,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.65) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* Content — sits above backdrop + vignette. */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        {/* Compact round pill. Removed the previous "LOADOUT / Three
            items. Use them in this round, or lose them." block —
            items belong in-flow, not in a setup-screen header. */}
        <div style={{ textAlign: 'center', padding: '8px 4px 0', maxWidth: 520, margin: '0 auto' }}>
          <div
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.6,
              textTransform: 'uppercase',
              color: '#FFC940',
              background: 'rgba(255,201,64,0.10)',
              border: '1px solid rgba(255,201,64,0.35)',
            }}
          >
            Round {matchNumber}
          </div>
        </div>

        {/* Self loadout reels — the centerpiece. Underdog reels (last
            place, where Legendary weight > 0) get a purple shimmer
            during the spin to telegraph the rubber-band: "something
            big might be coming." */}
        {selfRoll ? (
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
            <LoadoutReels
              items={selfRoll}
              size="large"
              onLand={handleSelfReelLand}
              matchKey={matchKey}
              underdog={selfIsUnderdog}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: 24, opacity: 0.65, fontSize: 13 }}>
            Watching the table.
          </div>
        )}

        {/* Phase 4 — tap-to-deploy strip. One tile per landed reel.
            Mario-Kart-style single-tap deploy: tile pulses, fires
            DEPLOY_ITEM immediately, server-echo flips the tile to
            ✓ DEPLOYED. No arming step. Only LOADED-window items are
            deployable in v1; PERSISTENT / REACTIVE items wire in
            later phases. */}
        {selfSeat && selfRoll ? (
          <DeployStrip
            roll={selfRoll}
            landedSlots={selfLandedSlots}
            pendingDeployments={selfSeat.pendingDeployments}
            onDeploy={(_idx, itemId) => {
              if (!roomId || !playerId) return;
              submitWheelCommand(roomId, 'DEPLOY_ITEM', playerId, { itemId })
                .catch((err) => {
                  console.warn('[WheelLoadoutView] DEPLOY_ITEM failed', err);
                });
            }}
          />
        ) : null}

        {/* Phase 2 — dial-wheel stake slider. Self-seat only; the
            AFK auto-default lands server-side at STAKE → WHEEL_REVEAL
            for any seat that doesn't drive this UI. */}
        {selfSeat ? (
          <WheelStakeSlider
            tokens={selfSeat.tokens}
            pendingStake={selfSeat.pendingStake}
            roomId={roomId}
            playerId={playerId}
          />
        ) : null}

        {/* Cast row — bots' loadouts roll in parallel. Threat-board
            mode kicks in once we wire targeting in phase 3. */}
        {otherSeats.length > 0 ? (
          <div style={{ maxWidth: 720, margin: '32px auto 0' }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                opacity: 0.6,
                textAlign: 'center',
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              At the table
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${otherSeats.length}, 1fr)`,
                gap: 8,
              }}
            >
              {otherSeats.map((seat) => {
                const roll = seatRolls.get(seat.seatId);
                if (!roll) return null;
                const position = seatPositions.get(seat.seatId) ?? 1;
                const underdog = isUnderdogPosition(position, finalLap);
                return (
                  <CastTile
                    key={seat.seatId}
                    seat={seat}
                    roll={roll}
                    matchKey={matchKey}
                    underdog={underdog}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* Final Lap overlay — sibling-positioned at the root so its
          fixed-position fullscreen layer sits above the loadout
          content. Renders only when triggerKey is non-null. */}
      <FinalLapOverlay triggerKey={finalLapTriggerKey} />

      {/* Legendary fullscreen flash — fires once per match if any of
          the self reels lands on a Legendary item. Subordinate visual
          beat to the FinalLapOverlay (which can fire alongside it),
          but uses its own non-blocking layer. */}
      <LegendaryFlash triggerKey={legendaryFlashKey} />
    </div>
  );
}

/**
 * Fullscreen flash that fires once when {@code triggerKey} transitions
 * to a non-null value. The "you got a Legendary" beat — sits above the
 * cast row + headline so the moment is impossible to miss, but is
 * brief (~1.1s total) and pointer-events:none so it can't intercept
 * any accidental taps.
 *
 * <p>Phase-4 candidate: layer in a horn sting + slow-mo of the
 * landed reel (camera punch-in). For now the flash is enough to
 * sell the moment in felt-tests.
 */
function LegendaryFlash({ triggerKey }: { triggerKey: string | null }) {
  const [visibleKey, setVisibleKey] = useState<string | null>(null);

  useEffect(() => {
    if (triggerKey === null) return;
    setVisibleKey(triggerKey);
    const id = window.setTimeout(() => setVisibleKey(null), 1100);
    return () => window.clearTimeout(id);
  }, [triggerKey]);

  if (visibleKey === null) return null;

  return (
    <div
      key={visibleKey}
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        pointerEvents: 'none',
        // Radial gold flash, washes out from centre and fades.
        background:
          'radial-gradient(ellipse at center, rgba(180, 117, 255, 0.55) 0%, rgba(180, 117, 255, 0.25) 35%, rgba(0,0,0,0) 70%)',
        mixBlendMode: 'screen',
        animation: 'gtTierFlashLegendary 1.1s ease-out forwards',
      }}
    />
  );
}

/**
 * Hook — produces a stable per-seat 3-item roll for the current match,
 * position-weighted via the rubber-band table. Phase-2 still rolls
 * client-side; phase 2 wrap-up (post felt-test) mirrors this on the
 * server inside ROLL_LOADOUT.
 *
 * <p>The map is regenerated when matchNumber changes so the next match
 * gets fresh rolls. Position changes mid-render do NOT trigger a
 * re-roll (positions ref-equality is stable within a render cycle —
 * the parent recomputes via {@code useMemo}).
 */
function useSeatRolls(
  seats: ReadonlyArray<WheelSeatView>,
  matchNumber: number,
  positions: ReadonlyMap<number, number>,
  finalLap: boolean,
): ReadonlyMap<number, readonly [ItemId, ItemId, ItemId]> {
  return useMemo(() => {
    const out = new Map<number, readonly [ItemId, ItemId, ItemId]>();
    for (const seat of seats) {
      if (seat.kind === 'EMPTY') continue;
      // Phase 4 — prefer the server-rolled loadout when present.
      // Falls back to a client-side roll only for dev previews / the
      // brief frame before the SSE delta arrives.
      if (seat.loadout.length === 3) {
        out.set(seat.seatId, [
          seat.loadout[0]!,
          seat.loadout[1]!,
          seat.loadout[2]!,
        ] as const);
        continue;
      }
      const position = positions.get(seat.seatId) ?? 1;
      out.set(seat.seatId, rollLoadoutForPosition(position, { finalLap }));
    }
    return out;
  }, [seats, matchNumber, positions, finalLap]);
}

function pickRandomItem(): ItemId {
  // Cycle through the full catalog during reel spin so any rolled
  // tier reads as plausible mid-spin. The landed item is determined
  // by rollLoadoutForPosition (position-weighted), not this function.
  // ALL_ITEMS is non-empty by construction; the ?? fallback satisfies
  // noUncheckedIndexedAccess.
  const idx = Math.floor(Math.random() * ALL_ITEMS.length);
  return ALL_ITEMS[idx] ?? 'COIN_STASH';
}

/**
 * The 3-reel landing animation. Each reel spins at staggered durations
 * (1500/2200/3200ms — reel 3 is the "anchor" with the most drama),
 * cycling through random glyphs at {@link REEL_TICK_INTERVAL_MS} until
 * its land time, then snaps to {@code items[idx]} with a {@code gtPop}
 * flourish.
 */
function LoadoutReels({
  items,
  size,
  onLand,
  matchKey,
  underdog = false,
}: {
  items: readonly [ItemId, ItemId, ItemId];
  size: ReelSize;
  onLand?: (idx: number, itemId: ItemId) => void;
  /** Changing this key restarts the animation (e.g. on matchNumber change). */
  matchKey: string;
  /** True when this seat is at a position where Legendary weight > 0.
   *  Drives the purple shimmer telegraph during the spin. */
  underdog?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: size === 'large' ? 12 : 4 }}>
      {items.map((id, idx) => (
        <Reel
          key={`${matchKey}-${idx}`}
          targetItem={id}
          // idx is 0..2 by construction (items is a fixed 3-tuple);
          // the ?? fallback satisfies noUncheckedIndexedAccess.
          spinDurationMs={REEL_SPIN_DURATIONS_MS[idx] ?? 2000}
          size={size}
          onLand={onLand ? () => onLand(idx, id) : undefined}
          underdog={underdog}
        />
      ))}
    </div>
  );
}

/**
 * One reel. Renders a square tile that ticks through random glyphs
 * during its spin window, then snaps to the target item with a pop.
 */
function Reel({
  targetItem,
  spinDurationMs,
  size,
  onLand,
  underdog = false,
}: {
  targetItem: ItemId;
  spinDurationMs: number;
  size: ReelSize;
  onLand?: () => void;
  /** True when Legendary tier is reachable for this seat — drives the
   *  shimmer animation during the spin. Off after landing. */
  underdog?: boolean;
}) {
  const dims = REEL_DIMS[size];
  const [currentItem, setCurrentItem] = useState<ItemId>(() => pickRandomItem());
  const [landed, setLanded] = useState(false);
  const onLandRef = useRef(onLand);
  onLandRef.current = onLand;

  useEffect(() => {
    setLanded(false);
    setCurrentItem(pickRandomItem());

    const tickId = window.setInterval(() => {
      setCurrentItem(pickRandomItem());
    }, REEL_TICK_INTERVAL_MS);

    const landTimeoutId = window.setTimeout(() => {
      window.clearInterval(tickId);
      setCurrentItem(targetItem);
      setLanded(true);
      onLandRef.current?.();
    }, spinDurationMs);

    return () => {
      window.clearInterval(tickId);
      window.clearTimeout(landTimeoutId);
    };
    // targetItem is stable for the lifetime of a match. spinDurationMs
    // is constant per reel position. Effect re-runs only on remount
    // (matchKey change at the parent).
  }, [targetItem, spinDurationMs]);

  const item = ITEMS[currentItem];
  const tierMeta = ITEM_TIER_META[item.tier];
  const frameColor = landed ? tierMeta.accent : 'rgba(255,255,255,0.35)';
  const frameGlow = landed
    ? `0 0 16px ${tierMeta.accent}55, inset 0 0 0 1px rgba(255,255,255,0.1)`
    : `inset 0 0 0 1px rgba(255,255,255,0.06)`;

  // Animation precedence (tile inner):
  //   landed       → gtPop one-shot (the snap flourish)
  //   underdog     → gtLegendaryShimmer infinite (rubber-band telegraph)
  //   otherwise    → none
  // gtLegendaryShimmer paints its own border-color + box-shadow, so we
  // skip the static frame styles for underdog reels mid-spin to avoid
  // fighting the keyframe.
  const tileAnimation = landed
    ? 'gtPop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)'
    : underdog
      ? 'gtLegendaryShimmer 1.4s ease-in-out infinite'
      : 'none';
  const useShimmerFrame = underdog && !landed;

  // Phase-3 — tier-specific reveal flair on land.
  //   COMMON     → just the gtPop tile snap (no extra ring or shake).
  //   UNCOMMON   → coin-burst ring expands out from the tile.
  //   RARE       → bigger gold ring + container shake.
  //   LEGENDARY  → biggest ring + harder container shake. The
  //                fullscreen flash + camera punch is owned by the
  //                parent {@code WheelLoadoutView} so it fires once
  //                per legendary landing, not three times for a
  //                triple-legendary roll (cap-prevented anyway, but
  //                belt-and-braces).
  const tierTier = landed ? item.tier : null;
  const ringAnim =
    tierTier === 'UNCOMMON'
      ? 'gtTierRingUncommon 0.7s ease-out forwards'
      : tierTier === 'RARE'
        ? 'gtTierRingRare 0.85s ease-out forwards'
        : tierTier === 'LEGENDARY'
          ? 'gtTierRingLegendary 1.05s ease-out forwards'
          : null;
  const containerShake =
    tierTier === 'RARE'
      ? 'gtTierShakeRare 0.45s ease-in-out'
      : tierTier === 'LEGENDARY'
        ? 'gtTierShakeLegendary 0.7s ease-in-out'
        : null;

  return (
    <div
      style={{
        position: 'relative',
        width: dims.tile,
        height: dims.tile,
        // Container-level shake when landing on RARE / LEGENDARY tiers.
        // gtPop on the tile inside still plays — they're separate
        // elements so the animations compose cleanly.
        animation: containerShake ?? undefined,
      }}
    >
      {/* Tier ring — absolutely positioned outside the tile bounds so
          its expansion paints over neighbouring reel space without
          shifting layout. Renders only on land for non-COMMON tiers. */}
      {ringAnim ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            // Ring starts at tile bounds and scales outward. Negative
            // inset gives it a touch of bleed so its inner edge tracks
            // the tile's rounded corner.
            inset: -2,
            borderRadius: 14,
            border: `4px solid ${tierMeta.accent}`,
            // Soft glow on the ring tints the surrounding card with
            // the tier accent during the burst.
            boxShadow: `0 0 24px ${tierMeta.accent}88`,
            pointerEvents: 'none',
            transformOrigin: 'center center',
            animation: ringAnim,
          }}
        />
      ) : null}

      {/* The tile proper — glyph + (large-size only) name. */}
      <div
        style={{
          width: dims.tile,
          height: dims.tile,
          border: useShimmerFrame
            ? '2px solid transparent' /* shimmer keyframe drives borderColor */
            : `2px solid ${frameColor}`,
          borderRadius: 12,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.25) 100%)',
          boxShadow: useShimmerFrame ? undefined : frameGlow,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          transition: 'border-color 0.18s, box-shadow 0.18s',
          animation: tileAnimation,
        }}
        aria-label={landed ? `Landed: ${item.name}` : 'Spinning'}
      >
        <div style={{ fontSize: dims.glyph, lineHeight: 1 }} aria-hidden>
          {item.glyph}
        </div>
        {dims.nameFs > 0 && landed ? (
          <div
            style={{
              fontSize: dims.nameFs,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: '#E9F0FF',
              textAlign: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.name}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One rival's tile in the cast row — avatar + their 3 mini-reels.
 * Shares the {@link Reel} animation but at the small size variant.
 */
function CastTile({
  seat,
  roll,
  matchKey,
  underdog = false,
}: {
  seat: WheelSeatView;
  roll: readonly [ItemId, ItemId, ItemId];
  matchKey: string;
  /** True when this rival is at a position where Legendary weight > 0
   *  — drives the gold "loser cheer" spotlight on the tile + shimmer
   *  on their reels. */
  underdog?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '8px 4px',
        borderRadius: 12,
        background: underdog
          ? 'linear-gradient(180deg, rgba(255,201,64,0.12) 0%, rgba(255,255,255,0.04) 100%)'
          : 'rgba(255,255,255,0.04)',
        border: underdog
          ? '1px solid rgba(255,201,64,0.4)'
          : '1px solid rgba(255,255,255,0.08)',
        // Subtle gold breathing on underdog tiles — telegraphs to the
        // table that this rival might be loaded with something big.
        // Phase 3 layers in bot barks ("Watch out, [name]'s loaded!").
        animation: underdog
          ? 'gtUnderdogSpotlight 2s ease-in-out infinite'
          : 'none',
        // Lift the underdog tile a touch so it reads as the focal
        // point of the cast row.
        transform: underdog ? 'translateY(-2px)' : 'none',
        transition: 'transform 0.2s, background 0.2s, border-color 0.2s',
      }}
    >
      {seat.archetype ? (
        <CastAvatar archetype={seat.archetype} size={28} />
      ) : (
        // Player seat (no archetype) — generic placeholder. v1 is
        // bots-only with one human, so this branch is rare; phase 4
        // (meta-progression) introduces real player-avatar support.
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'linear-gradient(180deg, #FFC940 0%, #E89E1A 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#1A1304',
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          {seat.displayName.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          opacity: 0.85,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {seat.displayName}
      </div>
      <LoadoutReels
        items={roll}
        size="small"
        matchKey={matchKey}
        underdog={underdog}
      />
    </div>
  );
}

/**
 * Phase 4 — tap-to-deploy strip beneath the player's reels.
 *
 * <p>Renders one chip per item in the player's 3-slot roll. A chip
 * has four visual states keyed off two inputs:
 *
 * <ul>
 *   <li><b>Spinning</b> — reel not yet landed; chip is muted and
 *       non-interactive.
 *   <li><b>Ready</b> — reel landed, item is a LOADED-window deploy
 *       target, and hasn't been deployed yet. Tap once to arm.
 *   <li><b>Armed</b> — first tap committed. Glowing rim; tap again to
 *       fire (submits DEPLOY_ITEM).
 *   <li><b>Deployed</b> — server has acked the deploy (item appears
 *       in {@code pendingDeployments}); chip is locked.
 * </ul>
 *
 * <p>Items whose {@code fireWindow} isn't {@code LOADED} render in a
 * disabled / "auto-fires" state — the deploy gesture only applies to
 * Loaded items in this phase. Other items (Shield reactive, Coin
 * Stash persistent, etc.) wire in later phases.
 */
function DeployStrip({
  roll,
  landedSlots,
  pendingDeployments,
  onDeploy,
}: {
  roll: readonly [ItemId, ItemId, ItemId];
  landedSlots: ReadonlyArray<boolean>;
  pendingDeployments: ReadonlyArray<ItemId>;
  onDeploy: (idx: number, itemId: ItemId) => void;
}) {
  // Count per-id usages so the chip's "deployed" state respects
  // duplicates (rolled two BOOSTs → first deploy locks slot 0 but
  // slot 1's chip stays ready). Tracks a running counter across
  // slots so deployed-ness lines up with the SSE delta.
  const usedById = new Map<ItemId, number>();
  for (const id of pendingDeployments) {
    usedById.set(id, (usedById.get(id) ?? 0) + 1);
  }
  // Walk roll slots left-to-right and "consume" the matching deploys.
  // Each slot picks up its own deployed-flag in order.
  const consumed = new Map<ItemId, number>();
  const slotIsDeployed = roll.map((itemId) => {
    const cap = usedById.get(itemId) ?? 0;
    const taken = consumed.get(itemId) ?? 0;
    if (taken < cap) {
      consumed.set(itemId, taken + 1);
      return true;
    }
    return false;
  });

  // Optimistic "just-tapped" flash so the player sees instant
  // feedback even before the SSE delta echoes back the deployed
  // state. Cleared after a short flash window; the server's
  // pendingDeployments update then drives the persistent
  // ✓ DEPLOYED state.
  const [flashIdx, setFlashIdx] = useState<number | null>(null);
  useEffect(() => {
    if (flashIdx === null) return;
    const t = window.setTimeout(() => setFlashIdx(null), 600);
    return () => window.clearTimeout(t);
  }, [flashIdx]);

  return (
    <div
      style={{
        marginTop: 14,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        maxWidth: 520,
        margin: '14px auto 0',
        padding: '0 16px',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {roll.map((itemId, idx) => {
        const landed = landedSlots[idx] ?? false;
        const meta = ITEMS[itemId];
        const isLoaded = meta.fireWindow === 'LOADED';
        const deployed = slotIsDeployed[idx] ?? false;
        const flashing = flashIdx === idx;
        return (
          <DeployChip
            key={idx}
            itemId={itemId}
            landed={landed}
            isLoaded={isLoaded}
            deployed={deployed}
            flashing={flashing}
            onTap={() => {
              if (!landed || deployed || !isLoaded) return;
              setFlashIdx(idx);
              onDeploy(idx, itemId);
            }}
          />
        );
      })}
    </div>
  );
}

function DeployChip({
  itemId,
  landed,
  isLoaded,
  deployed,
  flashing,
  onTap,
}: {
  itemId: ItemId;
  landed: boolean;
  isLoaded: boolean;
  deployed: boolean;
  flashing: boolean;
  onTap: () => void;
}) {
  const meta = ITEMS[itemId];
  const tierAccent = ITEM_TIER_META[meta.tier].accent;
  const status: 'spinning' | 'ready' | 'flashing' | 'deployed' | 'auto' =
    !landed ? 'spinning'
    : deployed ? 'deployed'
    : !isLoaded ? 'auto'
    : flashing ? 'flashing'
    : 'ready';
  const interactive = status === 'ready';
  const label: string = (() => {
    switch (status) {
      case 'spinning': return 'SPINNING…';
      case 'deployed': return 'DEPLOYED';
      case 'flashing': return 'FIRING…';
      case 'auto':     return autoFireLabel(meta.fireWindow);
      case 'ready':    return 'TAP TO DEPLOY';
    }
  })();
  const opacity = status === 'spinning' || status === 'auto' ? 0.55 : 1;
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={!interactive}
      style={{
        appearance: 'none',
        cursor: interactive ? 'pointer' : 'default',
        padding: '8px 6px',
        borderRadius: 10,
        background:
          status === 'flashing'
            ? `linear-gradient(180deg, ${tierAccent}55 0%, ${tierAccent}22 100%)`
            : 'rgba(20,10,38,0.85)',
        border: `2px solid ${status === 'flashing' ? tierAccent : `${tierAccent}88`}`,
        boxShadow:
          status === 'flashing'
            ? `0 0 14px ${tierAccent}aa, 0 6px 14px rgba(0,0,0,0.45)`
            : '0 4px 10px rgba(0,0,0,0.4)',
        color: '#E9F0FF',
        opacity,
        textAlign: 'center',
        fontFamily: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        transition: 'transform 160ms cubic-bezier(0.2, 0.7, 0.4, 1.4), box-shadow 160ms ease-out',
        transform: status === 'flashing' ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      <span style={{ fontSize: 18 }} aria-hidden>{meta.glyph}</span>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>
        {meta.name.toUpperCase()}
      </span>
      <span
        style={{
          marginTop: 2,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: 1,
          color: status === 'flashing' ? tierAccent
            : status === 'deployed' ? '#9DE54E'
            : 'rgba(255,255,255,0.55)',
        }}
      >
        {status === 'deployed' ? (
          <>
            <Icon name="check" size={10} alt="" /> {label}
          </>
        ) : (
          label
        )}
      </span>
    </button>
  );
}

function autoFireLabel(fireWindow: (typeof ITEMS)[ItemId]['fireWindow']): string {
  switch (fireWindow) {
    case 'PERSISTENT': return 'AUTO';
    case 'INSTANT':    return 'INSTANT';
    case 'AUTO_BUFF':  return 'AUTO-BUFF';
    case 'REACTIVE':   return 'REACTIVE';
    case 'META':       return 'META';
    default:           return '·';
  }
}
